import React, { useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { Perf } from 'r3f-perf';

import SceneContent from '../3d/SceneContent';
import { ChartDataSlicer } from '../ChartDataSlicer';
import { FPSMonitor } from '../FPSMonitor';
import { useFlowaesStore } from '../../store/useFlowaesStore';
import { DBG_CLEAN } from '../../utils/dbgFlag';

export interface MainTradingCanvasProps { [key: string]: any; }

export const MainTradingCanvas: React.FC<MainTradingCanvasProps> = ({
  onOpenMidWallChart, onOpenBackWallChart, onOpenFloorChart, onOpenSideChart,
  dataToRender,
  navTotalPoints,
  scaleResetKey,
  isBackWallChartOpen, isFloorChartOpen, isSideChartOpen,
  isLoadingData,
  isLoadingDataRef,
  keepVisibleWhileLoading,
  seriesId,
  interactionMode,
  isCrosshairPinned,
  setIsCrosshairActive,
  setSelectedZoneId,
  handleUserInteraction,
  controlRotateSpeed,
  controlPanSpeed,
  controlZoomSpeed,
    
  isReturningToLive, setIsReturningToLive, setHistoryOffset,
    targetHistoryOffset, historyOffsetRef,
  hasReachedHistoryEnd,
  onOldestEdge,
  processedData,
  show3D,
  backwallStyle,
  showShadowFloor,
  showShadowSide,
  showMidLineWall,
  setShowMidLineWall,
  midLineWallColor,
  midLineWallOpacity,
  autoRotate,
  isCrosshairActive,
  crosshairRef,
  
  timeframe,
  cameraTweenTarget,
  setCameraTweenTarget,
  controlsTweenTarget,
  setControlsTweenTarget,
  cameraStates,
  saveCameraState,
  zones,
  setZones,
  selectedZoneId,
  setInteractionMode,
  timeSensitivity,
  radialMenuPos,
  setIsInitialGlideComplete,
  isInitialGlideComplete,
  isCurrentlyIdle,
  width3D,
  heightScale3D,
  zLimit3D,
  isCreator,
  showPerfPanel,
}) => {
  const storeShowPerfPanel = useFlowaesStore(s => s.showPerfPanel);
  // ?dbg=1&clean=1: sem painel do profiler nem r3f-perf (mede o peso do diagnóstico)
  const isPerfActive = !DBG_CLEAN && (showPerfPanel !== undefined ? showPerfPanel : storeShowPerfPanel);
  const timeDragPointerXRef = useRef<number | null>(null);

  return (
    <div className={`absolute inset-0 z-0 transition-opacity duration-300 ease-in-out ${isLoadingData && !keepVisibleWhileLoading ? 'opacity-30' : 'opacity-100'} ${interactionMode === 'crosshair' ? 'cursor-crosshair' : interactionMode === 'time' ? 'cursor-ew-resize' : 'cursor-default'}`}>
      <Canvas 
        shadows
        dpr={[1, 2]}
        camera={{ position: [0, 150, 6000], fov: 15, far: 50000 }} 
        gl={{ 
          antialias: true, 
          powerPreference: "high-performance",
          stencil: false,
          depth: true,
          alpha: false,
          precision: 'highp',
          localClippingEnabled: true,
          preserveDrawingBuffer: true
        }}
        onCreated={({ gl }) => {
          gl.localClippingEnabled = true;
        }}
        onPointerDown={() => {
           handleUserInteraction();
        }}
        onPointerMissed={() => {
           if (interactionMode === 'crosshair' && !isCrosshairPinned) setIsCrosshairActive(false);
           setSelectedZoneId(null);
        }}
      >
        <color attach="background" args={['#02040a']} />
        
        <ambientLight intensity={0.4} color={0xffffff} />
        <pointLight position={[0, 400, 800]} intensity={1.2} color="#00d9ff" />
        <pointLight position={[0, -400, -800]} intensity={0.8} color="#ff3333" />
        <spotLight position={[500, 500, 500]} angle={0.15} penumbra={1} intensity={0.6} color="#00ff88" />
        
        <ChartDataSlicer
          controlRotateSpeed={controlRotateSpeed}
          controlPanSpeed={controlPanSpeed}
          controlZoomSpeed={controlZoomSpeed} 
          processedData={dataToRender} 
          seriesId={seriesId}
          isLoadingData={isLoadingData}
          isLoadingDataRef={isLoadingDataRef}
          isReturningToLive={isReturningToLive} setIsReturningToLive={setIsReturningToLive} setHistoryOffset={setHistoryOffset}
          targetHistoryOffset={targetHistoryOffset} historyOffsetRef={historyOffsetRef}
          hasReachedHistoryEnd={hasReachedHistoryEnd}
          onOldestEdge={onOldestEdge}
          totalPoints={navTotalPoints ?? dataToRender.length}
          scaleResetKey={scaleResetKey}
          show3D={show3D}
          onOpenMidWallChart={onOpenMidWallChart} onOpenBackWallChart={onOpenBackWallChart} onOpenFloorChart={onOpenFloorChart} onOpenSideChart={onOpenSideChart}
          isBackWallChartOpen={isBackWallChartOpen} isFloorChartOpen={isFloorChartOpen} isSideChartOpen={isSideChartOpen}
          backwallStyle={backwallStyle}
          showShadowFloor={showShadowFloor} 
          showShadowSide={showShadowSide} 
          showMidLineWall={showMidLineWall}
          setShowMidLineWall={setShowMidLineWall}
          midLineWallColor={midLineWallColor}
          midLineWallOpacity={midLineWallOpacity}
          interactionMode={interactionMode as any}
          autoRotate={autoRotate}
          isCrosshairActive={isCrosshairActive}
          setIsCrosshairActive={setIsCrosshairActive}
          isCrosshairPinned={isCrosshairPinned}
          crosshairRef={crosshairRef}
          
          
          timeframe={timeframe}
          cameraTweenTarget={cameraTweenTarget}
          setCameraTweenTarget={setCameraTweenTarget}
          controlsTweenTarget={controlsTweenTarget}
          setControlsTweenTarget={setControlsTweenTarget}
          cameraStates={cameraStates}
          saveCameraState={saveCameraState}
          zones={zones}
          setZones={setZones}
          selectedZoneId={selectedZoneId}
          setSelectedZoneId={setSelectedZoneId}
          setInteractionMode={setInteractionMode}
          timeSensitivity={timeSensitivity}
          isRadialMenuOpen={!!radialMenuPos}
          setIsInitialGlideComplete={setIsInitialGlideComplete}
          isInitialGlideComplete={isInitialGlideComplete}
          isCurrentlyIdle={isCurrentlyIdle}
          handleUserInteraction={handleUserInteraction}
          width3D={width3D}
          heightScale3D={heightScale3D}
          zLimit3D={zLimit3D}
        />
        {isCreator && isPerfActive && <Perf position="top-left" />}
      </Canvas>
      {isPerfActive && <FPSMonitor warningThreshold={45} />}
      
    </div>
  );
};
